import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, Card, CardContent, CardHeader, Divider, FormHelperText, Grid, IconButton, Link, TextField as MuiTextField, Tooltip, Typography, useTheme } from "@mui/material";
import { HttpError, ResourceComponentPropsWithId, useEditContext, useDeleteWithConfirmController, useRedirect, useResourceContext } from "ra-core";
import { FC, useEffect, useState } from "react";
import { Content } from "@/components/content/Content";
import ReadonlyField from "@/components/input/ReadonlyField";
import { RecPatch } from "@/dataProvider/tools";
import { McpServerClientDto, McpServerInfoUpdateDto } from "@/gen/api/secureai";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { useResourceRegister } from "@/utils/resourceRegister";
import { genUrlWithHashid } from "@/utils/hashid";
import * as Yup from 'yup';
import Edit from "@/controller/Edit";
import { StateErrors, StateTouched, usePlainStateFormUtils } from "@/components/input/usePlainStateFormUtils";
import WithSave from '@/controller/WithSave';
import ViewLogsCard from '@/components/secureai/ViewLogsCard';
import Confirm from '@/components/widgets/Confirm';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';


const MCPServerDetail: FC<ResourceComponentPropsWithId> = (props) => {
  const { id, resource } = props
  useResourceRegister({ name: 'secure-ai-error-logs' })

  return (
    <Edit id={id} resource={resource} basePath="" mutationMode="pessimistic" >
      <MCPServerDetailBody {...props} />
    </Edit>
  )
}

const validationSchema =
  Yup.object().shape({
    name: Yup.string().required().max(128),
    description: Yup.string().max(512),
  })

const initialS = (mcpServerInfo: McpServerClientDto|undefined) => ({
  prevData: mcpServerInfo,
  stage: {
    clientId: mcpServerInfo?.clientId ?? '',
    name: mcpServerInfo?.name ?? '',
    description: mcpServerInfo?.description ?? '',
  },
  errors: {} as StatesError,
  touched: {} as StatesTouched,
  editMode: false,
})

const MCPServerDetailBody: FC<ResourceComponentPropsWithId> = ({
  options,
}) => {
  const { record: mcpServerInfo, refetch } = useEditContext<McpServerClientDto&RecPatch>()
  const redirect = useRedirect()
  const baseResource = useResourceContext()
  const [states, setStates] = useState(() => ({...initialS(mcpServerInfo), openDeleteConfirm: false}))
  useEffect(() => {
    setStates(v => v.prevData !== mcpServerInfo ? {...initialS(mcpServerInfo), openDeleteConfirm: false} : v)
  }, [mcpServerInfo])

  const delHandlers = useDefaultSavedHandlers('Delete MCP Server')
  delHandlers.onSuccess.merge(() => redirect('/secure-ai/dashboard'))

  const {
    open,
    loading: deleteLoading,
    handleDelete,
    handleDialogClose: handleDeleteDialogClose,
    handleDialogOpen: handleDeleteDialogOpen,
  } = useDeleteWithConfirmController({
    resource: baseResource ?? 'secure-ai/mcp-servers',
    record: mcpServerInfo as any,
    ...delHandlers,
  })

  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, validationSchema)
  const handleSave = (save: (x: McpServerInfoUpdateDto) => Promise<void>) => async () => {
    const v1 = await trySubmitValidate()
    try {
      await save(v1.stage)
      if (refetch) {
        refetch()
      }
      setStates(v => ({...v, editMode: false }))
    } catch (error: any) {
      const hErr : HttpError = error
      setStates(v => ({...v, errors: {...v.errors, submit: hErr.message}}))
    }
  }
  const format = useUserTimeFormat()
  const theme = useTheme()
  const cardBgFix = theme.palette.mode === 'dark' && {
    sx: {
      bgcolor: '#121b25',
    }
  }


  if (!mcpServerInfo) { return null }
  return (
    <Content 
      pageName="MCP Server Detail" 
      resourceName="Detail" 
      breadcrumbs={options?.breadcrumbs}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card {...cardBgFix}>
            <CardHeader title={mcpServerInfo.name}
              action={
                states.editMode
                ? <>
                    <Button color="primary" variant="outlined" sx={{ mr: 2 }}
                      onClick={() => setStates(v => ({...v, editMode: false}))}
                    >
                      Cancel
                    </Button>
                    <WithSave async render={({save, saving}) =>
                      <Button color="primary" variant="contained" size="small"
                        disabled={saving}
                        onClick={handleSave(save)}
                      >
                        Save
                      </Button>
                    } />
                  </>
                : <>
                    <Tooltip title="Delete MCP Server">
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={(e: any) => handleDeleteDialogOpen(e)}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit MCP Server Info">
                      <IconButton color="primary" size="small"
                        onClick={() => setStates(v => ({...v, editMode: true}))}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={4}>
                {states.editMode &&
                  <>
                    <Grid item xs={12} container spacing={4}>
                      <Grid item xs={12} sm={6}>
                        <MuiTextField label="MCP Server Name" fullWidth 
                          {...fieldProps('name')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <MuiTextField label="Description" fullWidth 
                          {...fieldProps('description')}
                          />
                      </Grid>
                    </Grid>
                    {states.errors.submit &&
                      <Box sx={{ mt: 1.5, ml: 4 }}>
                        <FormHelperText error>{states.errors.submit}</FormHelperText>
                      </Box>}
                    <Divider sx={{ width: 'calc(100% - 32px)', mt: 4, ml: 4 }} />  
                  </>
                  }
                <Grid item xs={12} sm={6} container>
                  <ReadonlyField label="Database Name" value={mcpServerInfo.databaseName ?? ''} />
                  {!mcpServerInfo.databaseId ? null :
                    <Link variant="caption" color="info.main" marginLeft="auto" target="_blank"
                      href={genUrlWithHashid(`/encryption/mappings/$0/show`,
                        mcpServerInfo.databaseId || 0, mcpServerInfo.databaseName
                      )}
                    >
                      View Encryption Mappings
                    </Link>}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReadonlyField label="Database Engine/Edition" value={mcpServerInfo.databaseEngine ?? ''} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReadonlyField label="Database Server Address" value={mcpServerInfo.serverAddress ?? ''} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <ReadonlyField label="Database Server Port" value={mcpServerInfo.port ?? ''} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReadonlyField label="Driver Client Name" value={mcpServerInfo.driverClientName ?? ''} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <ReadonlyField label="Driver Operating Mode" value={mcpServerInfo.driverOperatingMode === 0 ? 'Decryption' : 'Encryption'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReadonlyField label="MCP Server Url" value={mcpServerInfo.url ?? ''} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <ReadonlyField label="Status" value={mcpServerInfo.onlineStatus ?? ''} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <ReadonlyField label="Status Refresh Time" value={format(mcpServerInfo.lastReportTime ?? 0, 'PPp')} />
                </Grid>
                {!states.editMode &&
                  <Grid item xs={12} sm={9}>
                    <ReadonlyField label="Description" value={mcpServerInfo.description ?? ''} multiline />
                  </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} >
          <ViewLogsCard clientId={mcpServerInfo.clientId!} />
        </Grid>
      </Grid>
      <Confirm
        isOpen={open}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onClose={(e: any) => {
          handleDeleteDialogClose(e);
          setStates(v => ({ ...v, openDeleteConfirm: false }));
        }}
        title="Delete Data Source"
        content={
          <Box>
            <Typography variant="body2">
              Are you sure you want to delete the Data Source "<b>{mcpServerInfo.name}</b>"?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              <i>This action cannot be undone.</i>
            </Typography>
          </Box>
        }
        confirm="Delete"
        cancel="Cancel"
      />
    </Content>
  )
}


export default MCPServerDetail;


interface UIState {
  clientId: string;
  name: string;
  description: string;
}
type StatesError = StateErrors<UIState>;
type StatesTouched = StateTouched<UIState>;
